import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { BroadcastChannel } from 'broadcast-channel';
import { ChangeLogEvent, ChangeLogDb, FileAttachment } from '@app/core/db/change-log';
import { Subject } from 'rxjs';

export interface Subscription {
  replay(): Promise<void>;
  dispose(): void;
}

@Injectable({
  providedIn: 'root',
})
export class ChangeLogService {
  private readonly topics: object;
  private db: ChangeLogDb;
  private syncChannel: BroadcastChannel;

  /** Emits whenever a new change is published. Components can use this to reactively update pending counts. */
  private _changes$ = new Subject<void>();
  readonly changes$ = this._changes$.asObservable();

  constructor() {
    this.topics = {};
    this.db = new ChangeLogDb();
    this.syncChannel = new BroadcastChannel('dwo-sync');
    this.syncChannel.onmessage = (message) => {
      if (message?.type === 'processed' || message?.type === 'synced') {
        this._changes$.next();
      }
    };
  }

  subscribe(topic: string, handler: (event: ChangeLogEvent) => void): Subscription {
    const subscribers = this.topics[topic] || [];
    subscribers.push(handler);
    this.topics[topic] = subscribers;

    return {
      replay: async () => {
        const events = await this.getPending(topic);
        for (const event of events) {
          handler(event);
        }
      },
      dispose: () => {
        const index = subscribers.indexOf(handler);

        if (index !== -1) {
          subscribers.splice(index, 1);
        }
      },
    };
  }

  subscribeAll(handler: (event: ChangeLogEvent) => void): Subscription {
    return this.subscribe('*', handler);
  }

  async getPendingCount(): Promise<number> {
    return this.db.events.where({ status: 'PENDING' }).count();
  }

  getPending(topic: string): Promise<ChangeLogEvent[]> {
    return this.db.events
      .filter((i) => i.status === 'PENDING')
      .filter((i) => i.refs.some((r) => r === topic))
      .toArray();
  }

  getAllPending(): Promise<ChangeLogEvent[]> {
    return this.db.events.where({ status: 'PENDING' }).toArray();
  }

  async publish<T>(
    type: string,
    content: T,
    topics: string[] = [],
    attachments: FileAttachment[] = null,
  ): Promise<string> {
    const status = 'PENDING';

    const time = new Date().getTime();

    const event: ChangeLogEvent = {
      key: uuid(),
      type,
      status,
      content,
      refs: topics,
      created: time,
      updated: time,
      attachments: null,
    };

    if (attachments) {
      event.attachments = attachments;
    }

    // persist event
    await this.db.events.add(event);

    // publish the event to the subscribers
    for (const topic of [...topics, '*']) {
      const subscribers = this.topics[topic];

      if (!subscribers) {
        continue;
      }

      subscribers.forEach((subscriber) => subscriber(event));
    }

    // process in the background (in the service worker. see sw-sync.js)
    // const registration = await navigator.serviceWorker.ready;
    // registration.sync.register('change_log');

    // instant processing (assume online)
    // request sync from the leader window/tab using the broadcasting api
    await this.syncChannel.postMessage({ type: 'sync' });

    this._changes$.next();

    return event.key;
  }
}
